import type { ToolUIPart } from 'ai';
import { isToolUIPart, type UIDataTypes, type UIMessagePart, type UITools } from 'ai';
import type { ReactNode } from 'react';
import { FillSl1FormTool } from './fill-sl1-form-tool';
import { FillSl2FormTool } from './fill-sl2-form-tool';
import { FormFillingTool } from './form-filling-tool';
import { ImageGenerationTool } from './image-generation-tool';
import { ProcessFilesTool } from './process-files-tool';
import { renderToolPendingState } from './tool-pending-state';
import type { ToolOutputRenderer } from '../../lib/default-renderers';
interface ToolRendererWrapperProps {
  part: UIMessagePart<UIDataTypes, UITools>;
  toolName: string;
  children: ReactNode;
}

/**
 * Wrapper component that handles common tool rendering logic:
 * - Validates tool part
 * - Handles pending states
 * - Renders children when output is available
 */
function ToolRendererWrapper({
  part,
  toolName,
  children
}: ToolRendererWrapperProps) {
  if (!isToolUIPart(part)) return null;

  // Handle pending state
  if (part.state && ['input-streaming', 'input-available'].includes(part.state)) {
    return renderToolPendingState(toolName);
  }

  // Render children (the actual tool component) when output is available
  return children;
}
export interface CreateToolRendererOptions {
  toolName: string;
  Component: React.ComponentType<{
    part: ToolUIPart;
    messageUuid: string;
  }>;
}

/**
 * Creates a tool renderer function with common logic handled by ToolRendererWrapper.
 */
export function createToolRenderer({
  toolName,
  Component
}: CreateToolRendererOptions): ToolOutputRenderer {
  return function ToolRenderer(part, messageUuid) {
    return <ToolRendererWrapper part={part} toolName={toolName}>
        <Component part={part as ToolUIPart} messageUuid={messageUuid} />
      </ToolRendererWrapper>;
  };
}

/**
 * Creates default tool output renderers.
 * Apps can extend or override these by passing their own renderers to Messages component.
 */
export function createDefaultToolRenderers(): Record<string, ToolOutputRenderer> {
  return {
    processFiles: createToolRenderer({
      toolName: 'processFiles',
      Component: ProcessFilesTool
    }),
    fillSl1Form: createToolRenderer({
      toolName: 'fillSl1Form',
      Component: FillSl1FormTool
    }),
    fillSl2Form: createToolRenderer({
      toolName: 'fillSl2Form',
      Component: FillSl2FormTool
    }),
    formFilling: createToolRenderer({
      toolName: 'formFilling',
      Component: FormFillingTool
    }),
    image_generation: createToolRenderer({
      toolName: 'image_generation',
      Component: ImageGenerationTool
    })
  };
}